import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { encodeCompletionId } from '../lib/completion-id'
import { newMemberId } from '../lib/member-id'
import { newTaskId } from '../lib/task-id'
import { newMeta } from './defaults'
import { normalizeAppData } from './migrate'
import type { NewChore } from '../types'
import type {
  AppData,
  AppSettings,
  Completion,
  CustomTask,
  Member,
  NewMember,
  SyncMeta,
} from './types'

/**
 * The app store.
 *
 * Shape taken from `../semainier/src/store/store.ts` — `persist`,
 * `PERSIST_VERSION`, `normalizeAppData` — and none of its twelve versions of
 * migration history, which belong to an app that had one.
 *
 * The store is the source of truth for the UI and is never blocked on the
 * network. Nothing in this file awaits anything: actions are synchronous, and
 * the sync engine picks up whatever they marked `dirty`.
 */

/** Never rename it, and never write a second copy elsewhere — CLAUDE.md §2. */
export const STORAGE_KEY = 'maison_zen_v1'

/**
 * Bumped whenever the persisted shape changes; drives `migrate`.
 *
 * 2 — `tasks`, the household's own chores. A version-1 save has no such key and
 * reads back as an empty list, which is what that install actually had.
 *
 * 3 — `members`, the people in the household, and `assigneeId` on a chore. Both
 * read back as absent-means-empty from a version-2 save: no members yet, and
 * every chore belonging to the whole house, which is what that install meant.
 */
const PERSIST_VERSION = 3

/** Stamp a row as locally modified. Sync picks it up from here. */
function touch<T extends SyncMeta>(row: T): T {
  return { ...row, updatedAt: new Date().toISOString(), dirty: true }
}

/**
 * Retire a row without removing it.
 *
 * A hard delete cannot propagate: the other device never learns the row is gone
 * and pushes it straight back on its next sync. The tombstone is what travels,
 * and `purgeTombstones` drops it once it is old enough that no device can still
 * be unaware — `CLAUDE.md` §3.
 */
function softDelete<T extends SyncMeta>(row: T): T {
  const at = new Date().toISOString()
  return { ...row, updatedAt: at, deletedAt: at, dirty: true }
}

/** Rows a user should actually see. */
export function live<T extends SyncMeta>(rows: readonly T[]): T[] {
  return rows.filter((r) => r.deletedAt === null)
}

/** The ids of the boxes that are ticked, for the day and month views. */
export function doneIds(rows: readonly Completion[]): Set<string> {
  const ids = new Set<string>()
  for (const row of rows) {
    if (row.done && row.deletedAt === null) ids.add(row.id)
  }
  return ids
}

interface Actions {
  /**
   * Tick or un-tick one box.
   *
   * Un-ticking is `done: false`, never a delete: a hard delete cannot
   * propagate, and the other device would push the row straight back.
   *
   * `memberId` is passed in rather than read from `auth/household.ts` so the
   * store stays free of the auth layer and testable on its own.
   */
  setCompletion: (taskDate: string, taskId: string, memberId: string, done: boolean) => void
  /** `setCompletion` with the value flipped — the one the UI calls. */
  toggleCompletion: (taskDate: string, taskId: string, memberId: string) => void
  /**
   * Add a chore of the household's own.
   *
   * The id is minted here rather than accepted from the caller: it is permanent
   * in the strongest sense this app has — the middle component of every
   * completion id ever recorded against the chore — so there is exactly one
   * place it can come from (`src/lib/task-id.ts`).
   *
   * It is *returned* because minting it here leaves the caller with no other way
   * to name the row it just created, and one caller needs to: a chore put on
   * somebody else's list is followed by a notification that says which chore.
   * `''` means nothing was added.
   */
  addTask: (chore: NewChore) => string
  /**
   * Retire a chore. Its checkmarks stay where they are.
   *
   * Deliberately, and for the reason `CLAUDE.md` §4 gives about removing a
   * built-in task: the rows are simply never rendered again, so a chore deleted
   * by mistake and re-created does not take a year of history with it.
   */
  removeTask: (id: string) => void
  /**
   * Add somebody to the household.
   *
   * The id is minted here for the same reason a task's is, and one degree more
   * so: it is the third component of every completion id this person will ever
   * write, so there is exactly one place it can come from
   * (`src/lib/member-id.ts`).
   *
   * Offline like every other action. Only the household's owner may actually
   * push the row — RLS says so, not this function — but writing it locally
   * first is what lets a parent add a child on a train and have it arrive
   * later.
   */
  addMember: (member: NewMember) => void
  /** Editorial, and the only field of a member that is. */
  renameMember: (id: string, displayName: string) => void
  /**
   * Retire a member. Their checkmarks stay where they are.
   *
   * `selfMemberId` is passed in rather than read from `auth/household.ts`, the
   * same way `setCompletion` takes its member: the store stays free of the auth
   * layer and testable on its own. It is not decoration — `household_ids()`
   * skips a tombstoned row, so a household that removed its own owner would
   * lock every one of its devices out of its own data, with no way back that
   * does not involve the SQL editor.
   */
  removeMember: (id: string, selfMemberId: string) => void
  saveSettings: (patch: Partial<AppSettings>) => void
}

export type AppState = AppData & Actions

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      ...normalizeAppData(null),

      setCompletion: (taskDate, taskId, memberId, done) =>
        set((s) => {
          const id = encodeCompletionId({ taskDate, taskId, memberId })
          const existing = s.completions.find((row) => row.id === id)

          if (existing) {
            if (existing.done === done && existing.deletedAt === null) return s
            return {
              completions: s.completions.map((row) =>
                row.id === id ? touch({ ...row, done, deletedAt: null }) : row,
              ),
            }
          }

          const row: Completion = { id, taskDate, taskId, memberId, done, ...newMeta() }
          return { completions: [...s.completions, row] }
        }),

      toggleCompletion: (taskDate, taskId, memberId) => {
        const id = encodeCompletionId({ taskDate, taskId, memberId })
        const existing = get().completions.find((row) => row.id === id)
        const done = !(existing?.done && existing.deletedAt === null)
        get().setCompletion(taskDate, taskId, memberId, done)
      },

      addTask: (chore) => {
        const label = chore.label.trim()
        // A chore with nothing to read is a row the user can neither recognise
        // nor find again to delete. The form guards this too; the store is what
        // makes it true of every caller.
        if (!label) return ''

        const row: CustomTask = {
          ...chore,
          label,
          id: newTaskId(),
          ...newMeta(),
        }
        set((s) => ({ tasks: [...s.tasks, row] }))
        return row.id
      },

      removeTask: (id) =>
        set((s) => ({
          tasks: s.tasks.map((row) => (row.id === id ? softDelete(row) : row)),
        })),

      addMember: (member) =>
        set((s) => {
          const displayName = member.displayName.trim()
          // A member with nothing to read is a chip nobody can pick and a row
          // the owner cannot find again to remove. The form guards this too;
          // the store is what makes it true of every caller.
          if (!displayName) return s

          const row: Member = {
            ...member,
            displayName,
            username: member.username.trim().toLowerCase(),
            authEmail: member.authEmail.trim().toLowerCase(),
            id: newMemberId(),
            ...newMeta(),
          }
          return { members: [...s.members, row] }
        }),

      renameMember: (id, displayName) =>
        set((s) => {
          const name = displayName.trim()
          if (!name) return s
          return {
            members: s.members.map((row) =>
              row.id === id && row.displayName !== name
                ? touch({ ...row, displayName: name })
                : row,
            ),
          }
        }),

      removeMember: (id, selfMemberId) =>
        set((s) => {
          if (id === selfMemberId) return s
          return {
            members: s.members.map((row) => (row.id === id ? softDelete(row) : row)),
          }
        }),

      // Through `touch()` like everything else: a settings row saved without
      // `dirty: true` is a reminder hour the other device never learns about.
      saveSettings: (patch) => set((s) => ({ settings: touch({ ...s.settings, ...patch }) })),
    }),

    {
      name: STORAGE_KEY,
      version: PERSIST_VERSION,
      storage: createJSONStorage(() => localStorage),

      partialize: (s): AppData => ({
        completions: s.completions,
        tasks: s.tasks,
        members: s.members,
        settings: s.settings,
      }),

      migrate: (persisted) => normalizeAppData(persisted) as never,

      // `migrate` only runs when the stored version differs, so a save written
      // by *this* version would otherwise reach the store unread. Normalising
      // here as well means every path into the store goes through the same
      // defensive read — a hand-edited value or a half-written save then costs
      // one row rather than the app's ability to open.
      merge: (persisted, current) => ({ ...current, ...normalizeAppData(persisted) }),
    },
  ),
)
